import type { ReactNode } from 'react';
import {
  Armchair,
  Check,
  Hotel,
  Loader2,
  Plane,
  PlaneTakeoff,
  RotateCcw,
  Search,
  Ticket,
  TriangleAlert,
  Utensils,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import { fmtDuration, journeyOf } from '@/routes/disruptions/pnr-status';
import { formatFlightDateTime, formatFlightTime } from '@/lib/disruptions/airport-tz';
import type { DisruptionPnrGroupRow } from '@/lib/disruptions/types';
import type { OptionSegment } from '@/lib/rebooking/types';
import type { Reservation } from '@/lib/booking/types';
import { SeatHoldChip } from './seat-hold-chip';
import { BoardChip, Clock, FieldLabel } from './airport-ui';
import { toneClasses, type BoardTone } from './airport-tokens';

// The SINGLE re-accommodation surface for a resolved PNR, drawn as a JOURNEY
// RAIL: three stations on one continuous track — the flight they lost, the care
// they get overnight, the flight they now hold — with the resolution actions
// (Issue ticket / Change, or the ticket number once issued) on the same band.
//
// It absorbs what the old AssignedFlightStrip duplicated right above it: the new
// flight, its date, the seat, the recovery delta and the hold clock each appear
// exactly ONCE, here. The station chips name each station's ROLE in the story
// ("Disrupted" / "Overnight" / "New flight"); the PNR's STATE is said once, in
// the hero — the rail says it again only in colour.
//
// Pinned full-width under the hero; the hero's JourneyRibbon yields to it once a
// flight is chosen.

/** Onward-connection feasibility for the assigned re-accommodation (protect-to-final). */
export interface AssignedOnward {
  onwardFlightNumber?: string | undefined;
  onwardOrigin?: string | undefined;
  onwardDepartureUtc?: string | undefined;
  layoverMinutes?: number | undefined;
  connectionFeasible?: boolean | undefined;
  overnightAtHub?: boolean | undefined;
}

interface ReaccommodationDiagramProps {
  group: DisruptionPnrGroupRow;
  /** The PNR's live hotel reservation, when one exists (names the care station). */
  reservation?: Reservation | undefined;
  /** Live selected option legs — drive the multi-leg route + the real departure instant. */
  selectedSegments?: OptionSegment[] | undefined;
  /** Onward-connection feasibility when the assigned option only reaches a hub. */
  onward?: AssignedOnward | null | undefined;
  /** Overnight need derived locally from the chosen flight, before the
   *  authoritative `requiresHotel` denorm lands (cases reconciles it async). The
   *  hotel card already draws its "Hotel needed" state from this — the rail must
   *  agree, or the two disagree for the seconds between select and reconcile. */
  overnightProvisional?: boolean | undefined;
  /** The option only reaches a hub and the wait there crosses a night. */
  overnightAtHub?: boolean | undefined;
  recovery?: { label: string; warn: boolean } | null | undefined;
  isTicketed: boolean;
  ticketNumber?: string | undefined;
  /** Server-truth seat-hold deadline (ISO), present while SELECTED and unticketed. */
  holdExpiresAt?: string | undefined;
  onIssueTicket: () => void;
  ticketPending: boolean;
  onChangeSelection: () => void;
  /** #743: Acceptance gate state — `awaiting` means the passenger hasn't responded
   *  yet and the operator must either wait or use Force Accept before issuing. */
  acceptanceState?: 'none' | 'awaiting' | 'accepted' | 'declined' | undefined;
  /** The new flight already left with no ticket (PO 2026-11-03): no hold clock, no «Emitir
   *  ticket», no «Cambiar vuelo» over options that left too — one way out, a new search. */
  departed?: { flight: string; when: string } | undefined;
  onSearchFresh?: (() => void) | undefined;
  searchPending?: boolean | undefined;
}

/** One station on the rail. */
interface Station {
  key: string;
  testId: string;
  tone: BoardTone;
  icon: ReactNode;
  /** The station's role in the story — never the PNR's overall state. */
  role: string;
  /** The headline: a route for a flight, the hotel's name for care. */
  headline: string;
  /** Codes and routes are monospaced; a hotel's NAME is prose and is not. */
  headlineMono?: boolean;
  /** Struck through — this is the leg they lost. */
  headlineStruck?: boolean;
  /** Flight number + departure, or the room line. */
  detail?: ReactNode;
  /** Seat / recovery chips. */
  extras?: ReactNode;
}

export function ReaccommodationDiagram({
  group,
  reservation,
  selectedSegments,
  onward,
  overnightProvisional,
  overnightAtHub,
  recovery,
  isTicketed,
  ticketNumber,
  holdExpiresAt,
  onIssueTicket,
  ticketPending,
  onChangeSelection,
  acceptanceState,
  departed,
  onSearchFresh,
  searchPending,
}: ReaccommodationDiagramProps) {
  const { t } = useTranslation('pnr');
  const assigned = group.assignedFlight;
  const needsHotel =
    group.accommodationKind === 'HOTEL' ||
    group.requiresHotel === true ||
    !!reservation ||
    overnightProvisional === true ||
    overnightAtHub === true;
  const mealVoucher = group.accommodationKind === 'MEAL_VOUCHER';
  // WHERE the night falls (the verdict's hotel geo anchor). A night at the
  // CONNECTION HUB happens AFTER the new flight's first leg — the rail must say
  // so, or a same-day +5h reflight with an SCL night reads as an absurd
  // pre-departure hotel at the stranding airport.
  const hubNight =
    !mealVoucher && !!group.overnightStation && group.overnightStation !== group.origin;

  // New-flight facts — live option segments first (present immediately), the async
  // assignedFlight denorm as fallback.
  const journey = selectedSegments && selectedSegments.length > 1 ? journeyOf(selectedSegments) : null;
  const newRoute = journey
    ? [journey.from, ...journey.via, journey.to].join(' → ')
    : [assigned?.origin ?? group.origin, assigned?.destination ?? group.destination]
        .filter(Boolean)
        .join(' → ');
  const newFlightNumber = assigned?.number ?? selectedSegments?.[0]?.flightNumber ?? group.flightNumber;
  const newDepartureIso = selectedSegments?.[0]?.departure ?? assigned?.departureAt;
  const newDepartureOrigin = selectedSegments?.[0]?.origin ?? assigned?.origin;
  const newWhen = newDepartureIso ? formatFlightDateTime(newDepartureIso, newDepartureOrigin) : null;
  const oldWhen = group.departureAt ? formatFlightDateTime(group.departureAt, group.origin) : null;

  const stations: Station[] = [
    {
      key: 'disrupted',
      testId: 'diagram-disrupted',
      tone: 'alert',
      icon: <Plane className="h-5 w-5 rotate-45" aria-hidden />,
      role: t('diagram.role.disrupted'),
      headline: `${group.origin} → ${group.destination}`,
      headlineMono: true,
      headlineStruck: true,
      detail: (
        <>
          <span className="font-mono font-semibold">{group.flightNumber || group.flightLabel}</span>
          {oldWhen ? <> · {oldWhen}</> : null}
        </>
      ),
    },
  ];

  const careStation: Station | null =
    needsHotel || mealVoucher
      ? {
          key: 'care',
          testId: mealVoucher ? 'diagram-voucher' : 'diagram-hotel',
          tone: 'warn',
          icon: mealVoucher ? (
            <Utensils className="h-5 w-5" aria-hidden />
          ) : (
            <Hotel className="h-5 w-5" aria-hidden />
          ),
          // Name the station the night is spent at when it isn't the stranding
          // airport — "Overnight · SCL" tells the operator this room lives at the hub.
          role: mealVoucher
            ? t('diagram.role.mealVoucher')
            : hubNight
              ? t('diagram.role.overnightAt', { station: group.overnightStation })
              : t('diagram.role.overnight'),
          headline: mealVoucher
            ? t('diagram.mealVoucherHeadline')
            : (reservation?.hotelName ?? t('diagram.hotelToBeBooked')),
          detail: mealVoucher ? (
            t('diagram.mealDetail')
          ) : reservation ? (
            <>
              {t('diagram.roomsBooked', { count: reservation.rooms ?? 1 })}
              {hubNight ? t('diagram.atConnectionSuffix') : ''}
            </>
          ) : (
            t('diagram.bookFromCard')
          ),
        }
      : null;

  // A night at the stranding airport comes BEFORE the new flight; a night at the
  // connection hub comes AFTER its first leg — keep the rail chronological.
  if (careStation && !hubNight) stations.push(careStation);

  stations.push({
    key: 'new',
    testId: 'diagram-new-flight',
    tone: 'ok',
    icon: <PlaneTakeoff className="h-5 w-5" aria-hidden />,
    role: t('diagram.role.newFlight'),
    headline: newRoute,
    headlineMono: true,
    detail: (
      <>
        <span className="font-mono font-semibold">{newFlightNumber}</span>
        {newWhen ? (
          <>
            {' · '}
            <Clock data-testid="reaccom-when">{newWhen}</Clock>
          </>
        ) : (
          t('diagram.departureTbd')
        )}
      </>
    ),
    extras: (
      <>
        {recovery ? (
          // Delta only — the hero's status line already carries the
          // "⚠ Outside recovery window" words; the tone says it here.
          <BoardChip tone={recovery.warn ? 'alert' : 'ok'} data-testid="reaccom-recovery">
            {recovery.label}
          </BoardChip>
        ) : null}
        {assigned?.seat ? (
          <BoardChip tone="idle" data-testid="reaccom-seat">
            <Armchair className="h-3 w-3" aria-hidden />
            {assigned.seat}
            {assigned.cabin ? <span>{assigned.cabin.replace(/_/g, ' ').toLowerCase()}</span> : null}
          </BoardChip>
        ) : null}
      </>
    ),
  });

  if (careStation && hubNight) stations.push(careStation);

  return (
    <section
      // Dashed rule: the perforation on a boarding pass, where the stub tears off.
      className="border-t border-dashed border-border bg-muted/25 py-4 pl-6 pr-5 sm:pl-7 sm:pr-6"
      aria-label={t('diagram.ariaLabel')}
      data-testid="reaccommodation-diagram"
    >
      {/* Label + the resolution actions — the ONE place they live. */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <FieldLabel>{t('diagram.title')}</FieldLabel>
        <div className="flex flex-wrap items-center gap-2.5">
          {departed ? null : <SeatHoldChip holdExpiresAt={holdExpiresAt} isTicketed={isTicketed} />}
          {departed && !isTicketed ? (
            onSearchFresh ? (
              <Button
                type="button"
                size="sm"
                onClick={onSearchFresh}
                disabled={searchPending}
                data-testid="drawer-search-fresh"
              >
                {searchPending ? (
                  <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" aria-hidden />
                ) : (
                  <Search className="mr-1 h-3.5 w-3.5" aria-hidden />
                )}
                {t('diagram.searchFresh')}
              </Button>
            ) : null
          ) : isTicketed ? (
            // The ticket NUMBER is the fact worth carrying here; that it is
            // ticketed is already the hero's headline.
            <span
              className="flex items-center gap-2 rounded-md border border-emerald-500/30 bg-emerald-50 px-2.5 py-1 dark:bg-emerald-950/40"
              data-testid="drawer-ticketed"
            >
              <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden />
              <span className="leading-none">
                <FieldLabel className="text-emerald-700/70 dark:text-emerald-400/70">
                  {t('diagram.ticket')}
                </FieldLabel>
                <span className="mt-1 block font-mono text-xs font-bold text-emerald-800 dark:text-emerald-200">
                  {ticketNumber}
                </span>
              </span>
            </span>
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onChangeSelection}
                data-testid="drawer-cancel-selection"
              >
                <RotateCcw className="mr-1 h-3.5 w-3.5" aria-hidden />
                {t('diagram.changeFlight')}
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={onIssueTicket}
                disabled={ticketPending || acceptanceState === 'awaiting'}
                title={acceptanceState === 'awaiting' ? t('diagram.issueTicketAwaitingTitle') : undefined}
                data-testid="drawer-issue-ticket"
              >
                {ticketPending ? (
                  <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" aria-hidden />
                ) : (
                  <Ticket className="mr-1 h-3.5 w-3.5" aria-hidden />
                )}
                {t('diagram.issueTicket')}
              </Button>
            </>
          )}
        </div>
      </div>

      {departed && !isTicketed ? (
        <p
          className="-mt-1 mb-4 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/[0.06] px-3 py-2 text-sm text-destructive"
          role="status"
          data-testid="drawer-flight-departed"
        >
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>{t('diagram.departed', departed)}</span>
        </p>
      ) : null}

      {/* The rail: lost flight → care → new flight, on one continuous track. */}
      <ol
        className="grid gap-y-6 sm:grid-flow-col sm:auto-cols-fr sm:gap-y-0"
        aria-label={t('diagram.stationsAriaLabel')}
      >
        {stations.map((s, i) => {
          const tone = toneClasses(s.tone);
          return (
            <li
              key={s.key}
              className="relative flex items-start gap-3 sm:flex-col sm:items-center sm:gap-0 sm:px-2 sm:text-center"
              data-testid={s.testId}
            >
              {/* Track — one calm neutral line so the STATIONS carry the colour;
                  horizontal between stations on sm+, vertical on phone. */}
              {i > 0 ? (
                <span
                  className="absolute left-0 top-5 hidden h-[3px] w-1/2 bg-border sm:block"
                  aria-hidden
                />
              ) : null}
              {i < stations.length - 1 ? (
                <>
                  <span
                    className="absolute left-1/2 top-5 hidden h-[3px] w-1/2 bg-border sm:block"
                    aria-hidden
                  />
                  <span
                    className="absolute bottom-[-1.5rem] left-5 top-11 w-[3px] bg-border sm:hidden"
                    aria-hidden
                  />
                </>
              ) : null}

              {/* Station node. */}
              <span
                className={cn(
                  'relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 bg-background',
                  tone.border,
                  tone.text,
                )}
              >
                <span className={cn('absolute inset-0 rounded-full', tone.surface)} aria-hidden />
                <span className="relative">{s.icon}</span>
              </span>

              {/* Station content. */}
              <div className="min-w-0 flex-1 sm:mt-3 sm:w-full sm:flex-none">
                <BoardChip tone={s.tone}>{s.role}</BoardChip>
                <p
                  className={cn(
                    'mt-1.5 truncate text-lg font-bold leading-tight tracking-tight',
                    s.headlineMono && 'font-mono',
                    s.headlineStruck && 'text-muted-foreground line-through decoration-rose-500/70 decoration-2',
                  )}
                  title={s.headline}
                >
                  {s.headline}
                </p>
                {s.detail ? (
                  <p className="mt-0.5 truncate text-xs text-muted-foreground">{s.detail}</p>
                ) : null}
                {s.extras ? (
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5 sm:justify-center">
                    {s.extras}
                  </div>
                ) : null}
              </div>
            </li>
          );
        })}
      </ol>

      {/* Footnotes — the honest small print under the rail. */}
      {onward || assigned?.seatReason || group.connection?.finalDestination ? (
        <div className="mt-4 space-y-1 border-t border-border/70 pt-2.5">
          {onward ? <OnwardLine onward={onward} /> : null}
          {assigned?.seatReason ? (
            <p className="text-xs text-muted-foreground">{assigned.seatReason}</p>
          ) : null}
          {group.connection?.finalDestination &&
          group.connection.finalDestination !== (assigned?.destination ?? group.destination) ? (
            <p className="text-xs text-muted-foreground">
              {t('diagram.protectedTo')}{' '}
              <span className="font-mono font-semibold">{group.connection.finalDestination}</span>
              {group.connection.via?.length
                ? t('diagram.viaSuffix', { via: group.connection.via.join(', ') })
                : ''}
              {t('diagram.onwardKept')}
            </p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

/** "↳ then LA1051 09:40 · 7h48m layover ✓/✗" — relates the assigned re-accommodation
 *  to the PNR's surviving onward leg so the operator sees the whole journey. */
function OnwardLine({ onward: o }: { onward: AssignedOnward }) {
  const { t } = useTranslation('pnr');
  const when = o.onwardDepartureUtc ? formatFlightTime(o.onwardDepartureUtc, o.onwardOrigin) : undefined;
  const layover = typeof o.layoverMinutes === 'number' ? fmtDuration(o.layoverMinutes * 60_000) : undefined;
  const flight = o.onwardFlightNumber ?? t('diagram.onwardFallback');
  const ok = o.connectionFeasible;
  return (
    <p
      className={cn(
        'flex flex-wrap items-center gap-1 text-xs font-medium',
        ok === undefined
          ? 'text-muted-foreground'
          : ok
            ? 'text-emerald-700 dark:text-emerald-400'
            : 'text-rose-600 dark:text-rose-400',
      )}
      data-testid="reaccom-onward"
    >
      <span aria-hidden>↳</span>
      <span>
        {t('option.then')} <span className="font-mono">{flight}</span>
        {when ? ` ${when}` : ''}
        {layover ? t('option.layoverSuffix', { layover }) : ''}
      </span>
      {ok === undefined ? (
        <span className="font-normal">{t('diagram.notValidated')}</span>
      ) : ok ? (
        <span className="inline-flex items-center gap-0.5">
          <Check className="h-3 w-3" aria-hidden /> {t('diagram.feasible')}
          {o.overnightAtHub ? t('diagram.overnightAtHubSuffix') : ''}
        </span>
      ) : (
        <span className="inline-flex items-center gap-0.5">
          <TriangleAlert className="h-3 w-3" aria-hidden />
          {o.overnightAtHub ? t('diagram.overnightAtHub') : t('diagram.tightMayMiss')}
        </span>
      )}
    </p>
  );
}
